/**
 * The words for portal access, in one place so the staff Portal tab, the
 * contact picker and the customer portal all say the same thing
 * (naming.md: one concept, one name, in every layer).
 *
 * "Own work" is the basic access every linked person has: orders they placed,
 * quotes and proofs addressed to them. Everything else is an extra that only
 * Pollards staff can add.
 */
import type {
  PortalContactSuggestions,
  PortalExtra,
  PortalPerson,
  PortalPersonOption,
} from "../data/customer-portal-access-types";
import { currentTime } from "./current-time";

export const OWN_WORK_LABEL = "Their own orders and quotes";
export const OWN_WORK_DESCRIPTION =
  "Orders they placed, and quotes and proofs sent to them.";

export interface PortalExtraDefinition {
  key: PortalExtra;
  label: string;
  description: string;
}

/** Display order. Invoices and statements arrive with the sales ledger and are not listed until then. */
export const PORTAL_EXTRAS: readonly PortalExtraDefinition[] = [
  {
    key: "customer_wide_view",
    label: "See all of the customer's work",
    description:
      "Every quote, order and proof for the customer, including its other accounts.",
  },
  {
    key: "order_on_account",
    label: "Order on account",
    description:
      "Place catalogue orders on the customer's credit account instead of paying by card.",
  },
  {
    key: "spend_reporting",
    label: "See spend",
    description:
      "The Spend page: what the whole company has spent, by month. Works whether or not they see all the customer's work, and only while the customer's spend switch is on.",
  },
];

export function portalExtraLabel(extra: PortalExtra): string {
  return PORTAL_EXTRAS.find((e) => e.key === extra)?.label ?? extra;
}

/** The extras after switching `extra` on or off, in display order. The extras are independent of each other. */
export function toggleExtra(
  current: readonly PortalExtra[],
  extra: PortalExtra,
  on: boolean,
): PortalExtra[] {
  const next = new Set(current);
  if (on) next.add(extra);
  else next.delete(extra);
  return PORTAL_EXTRAS.map((e) => e.key).filter((k) => next.has(k));
}

/**
 * One line for what a person can do at a customer. Spend is shown as held but
 * switched off when the customer's spend switch is off, so staff do not read a
 * Spend page into someone who cannot see one.
 */
export function describePersonAccess(
  person: Pick<PortalPerson, "extras">,
  spendEnabled = true,
): string {
  const parts = [
    person.extras.includes("customer_wide_view")
      ? "All of the customer's work"
      : OWN_WORK_LABEL,
  ];
  if (person.extras.includes("spend_reporting"))
    parts.push(
      spendEnabled ? "spend" : "spend (customer's spend switch is off)",
    );
  if (person.extras.includes("order_on_account"))
    parts.push("orders on account");
  return parts.join(" · ");
}

/** What the person holds at one linked customer: the facts the server decided, as the portal receives them. */
export interface LinkedCustomerAccess {
  customer_name: string;
  has_customer_wide_view: boolean;
  can_order_on_account: boolean;
  can_see_spend: boolean;
}

/**
 * What a linked customer gives the person, in the customer's own words (the portal, not staff):
 * "Everything for Currys · order on account · spend", or "Your own orders, quotes and proofs".
 */
export function linkedCustomerAccessSummary(
  customer: LinkedCustomerAccess,
): string {
  const parts = [
    customer.has_customer_wide_view
      ? `Everything for ${customer.customer_name}`
      : "Your own orders, quotes and proofs",
  ];
  if (customer.can_order_on_account) parts.push("order on account");
  if (customer.can_see_spend) parts.push("spend");
  return parts.join(" · ");
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The "Want to ...?" offer on the welcome page. It names only what the person does not already hold,
 * and only the customers where they do not hold it: someone with everything and order on account at
 * one customer and the basics at another is offered more at the second and nothing at the first.
 * Null when there is nothing left to offer.
 */
export function portalUpsellOffer(
  customers: readonly Pick<
    LinkedCustomerAccess,
    "customer_name" | "has_customer_wide_view" | "can_order_on_account"
  >[],
): string | null {
  const withoutWideView = customers
    .filter((c) => !c.has_customer_wide_view)
    .map((c) => c.customer_name);
  const withoutOnAccount = customers
    .filter((c) => !c.can_order_on_account)
    .map((c) => c.customer_name);
  const lackBoth =
    withoutWideView.length > 0 &&
    withoutWideView.join("\0") === withoutOnAccount.join("\0");
  const wants = lackBoth
    ? [`see everything for ${joinNames(withoutWideView)} or order on account`]
    : [
        withoutWideView.length > 0
          ? `see everything for ${joinNames(withoutWideView)}`
          : null,
        withoutOnAccount.length > 0
          ? `order on account for ${joinNames(withoutOnAccount)}`
          : null,
      ].filter((want): want is string => want !== null);
  if (wants.length === 0) return null;
  return `Want to ${wants.join(", or ")}? Ask your Pollards contact, or call us on 01752 663111.`;
}

/** "today", "yesterday", "5 days ago": how long since a sign-in, without a date library. */
export function daysAgoLabel(
  iso: string,
  now: number = currentTime().getTime(),
): string {
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  return `${Math.round(days / 30)} months ago`;
}

/** Emails match on trim + lowercase only: no plus-address stripping, no domain matching. */
export function normaliseContactEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function looksLikeEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());
}

// ---- The contact picker's lists ------------------------------------------

export interface LinkedPersonOption {
  person_id: string;
  name: string;
  email: string;
  /** The customer's default order contact: listed first (v1 completion D2). */
  is_default_order_contact?: boolean;
  job_title?: string | null;
}

export interface GroupedContactOptions {
  /** People already at the customer, matching the query: the default order contact first. */
  linked: LinkedPersonOption[];
  /** People known from other customers: choosing one links them here too. */
  known: PortalPersonOption[];
  /** An email typed in full that matches no one: offer to add it. Null otherwise. */
  typedNewEmail: string | null;
}

/**
 * Orders the picker's choices and removes the duplicates its sources produce
 * between them: a person already at the customer (the host's own list, or the
 * lookup's) appears once, the default order contact first, never also as a
 * known person; an email typed in full that matches anyone is that person, not
 * a new one. Tharstern contacts are people once the people feed has run (v1
 * completion D1), so they are no group of their own. Shared by every rendering
 * of the picker so they cannot disagree about who is on offer.
 */
export function groupContactOptions(input: {
  query: string;
  linkedPeople: readonly LinkedPersonOption[];
  suggestions: PortalContactSuggestions;
  /** False when there is no lookup behind the picker: typed emails are then not accepted. */
  canAddNewEmail: boolean;
}): GroupedContactOptions {
  const q = input.query.trim().toLowerCase();
  const fromLookup: LinkedPersonOption[] = input.suggestions.people
    .filter((p) => p.linked_here)
    .map((p) => ({
      person_id: p.person_id,
      name: p.display_name,
      email: p.email,
      is_default_order_contact: p.is_default_order_contact ?? false,
      job_title: p.job_title ?? null,
    }));
  const byEmail = new Map<string, LinkedPersonOption>();
  for (const person of [...input.linkedPeople, ...fromLookup]) {
    const email = normaliseContactEmail(person.email);
    const seen = byEmail.get(email);
    // The lookup knows who the default is and their job title; the host's own entry keeps its name.
    byEmail.set(
      email,
      seen
        ? {
            ...person,
            ...seen,
            is_default_order_contact:
              seen.is_default_order_contact || person.is_default_order_contact,
            job_title: seen.job_title ?? person.job_title,
          }
        : person,
    );
  }
  const linked = [...byEmail.values()]
    .filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q),
    )
    .sort(
      (a, b) =>
        Number(b.is_default_order_contact ?? false) -
        Number(a.is_default_order_contact ?? false),
    );
  const linkedEmails = new Set(byEmail.keys());
  const known = input.suggestions.people.filter(
    (p) => !p.linked_here && !linkedEmails.has(normaliseContactEmail(p.email)),
  );
  const knownEmails = new Set(known.map((p) => normaliseContactEmail(p.email)));
  const typed = normaliseContactEmail(input.query);
  const typedMatchesSomeone = linkedEmails.has(typed) || knownEmails.has(typed);
  return {
    linked,
    known,
    typedNewEmail:
      input.canAddNewEmail &&
      looksLikeEmail(input.query) &&
      !typedMatchesSomeone
        ? typed
        : null,
  };
}

/** "Sam Lee" from "sam.lee@x.co.uk": a starting point staff can overwrite. */
export function displayNameFromEmail(email: string): string {
  const local = normaliseContactEmail(email).split("@")[0] ?? "";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
